import { Vector4 } from "three";
import { Vector3 } from "three";
import { Vector2 } from "three";
import { Attribute, CoreAttribute } from "./Attribute";
import { CoreEntity } from "./Entity";
import { CoreType } from "../Type";
const ATTRIB_NAMES = {
  POSITION: "position",
  NORMAL: "normal"
};
var ComponentName = /* @__PURE__ */ ((ComponentName2) => {
  ComponentName2["x"] = "x";
  ComponentName2["y"] = "y";
  ComponentName2["z"] = "z";
  ComponentName2["w"] = "w";
  ComponentName2["r"] = "r";
  ComponentName2["g"] = "g";
  ComponentName2["b"] = "b";
  return ComponentName2;
})(ComponentName || {});
const COMPONENT_INDICES = {
  x: 0,
  y: 1,
  z: 2,
  w: 3,
  r: 0,
  g: 1,
  b: 2
};
const DOT = ".";
export class CorePoint extends CoreEntity {
  constructor(_coreGeometry, _index) {
    super(_index);
    this._coreGeometry = _coreGeometry;
    this._geometry = this._coreGeometry.geometry();
  }
  applyMatrix4(matrix) {
    this.position().applyMatrix4(matrix);
  }
  coreGeometry() {
    return this._coreGeometry;
  }
  geometry() {
    return this._geometry = this._geometry || this._coreGeometry.geometry();
  }
  attribSize(name) {
    name = CoreAttribute.remapName(name);
    return this._geometry.getAttribute(name).itemSize;
  }
  hasAttrib(name) {
    const remapped_name = CoreAttribute.remapName(name);
    return this._coreGeometry.hasAttrib(remapped_name);
  }
  attribValue(name, target) {
    if (name === Attribute.POINT_INDEX) {
      return this.index();
    } else {
      let component_name = null;
      let component_index = null;
      if (name[name.length - 2] === DOT) {
        component_name = name[name.length - 1];
        component_index = COMPONENT_INDICES[component_name];
        name = name.substring(0, name.length - 2);
      }
      const remaped_name = CoreAttribute.remapName(name);
      const attrib = this._geometry.getAttribute(remaped_name);
      if (attrib) {
        const { array } = attrib;
        if (this._coreGeometry.isAttribIndexed(remaped_name)) {
          return this.indexedAttribValue(remaped_name);
        } else {
          const size = attrib.itemSize;
          const start_index = this._index * size;
          if (component_index == null) {
            switch (size) {
              case 1:
                return array[start_index];
                break;
              case 2:
                target = target || new Vector2();
                target.fromArray(array, start_index);
                return target;
                break;
              case 3:
                target = target || new Vector3();
                target.fromArray(array, start_index);
                return target;
                break;
              case 4:
                target = target || new Vector4();
                target.fromArray(array, start_index);
                return target;
                break;
              default:
                throw `size not valid (${size})`;
            }
          } else {
            switch (size) {
              case 1:
                return array[start_index];
                break;
              default:
                return array[start_index + component_index];
            }
          }
        }
      } else {
        const message = `attrib ${name} not found. availables are: ${Object.keys(this._geometry.attributes || {}).join(",")}`;
        console.warn(message);
        throw message;
      }
    }
  }
  attribValueNumber(name) {
    const remapedName = CoreAttribute.remapName(name);
    const attrib = this._geometry.getAttribute(remapedName);
    return attrib.array[this._index];
  }
  attribValueVector2(name, target) {
    const remapedName = CoreAttribute.remapName(name);
    const attrib = this._geometry.getAttribute(remapedName);
    target.fromArray(attrib.array, this._index * 2);
    return target;
  }
  attribValueVector3(name, target) {
    const remapedName = CoreAttribute.remapName(name);
    const attrib = this._geometry.getAttribute(remapedName);
    target.fromArray(attrib.array, this._index * 3);
    return target;
  }
  attribValueVector4(name, target) {
    const remapedName = CoreAttribute.remapName(name);
    const attrib = this._geometry.getAttribute(remapedName);
    target.fromArray(attrib.array, this._index * 4);
    return target;
  }
  indexedAttribValue(name) {
    const value_index = this.attribValueIndex(name);
    return this._coreGeometry.userDataAttrib(name)[value_index];
  }
  stringAttribValue(name) {
    return this.indexedAttribValue(name);
  }
  attribValueIndex(name) {
    if (this._coreGeometry.isAttribIndexed(name)) {
      return this._geometry.getAttribute(name).array[this._index];
    } else {
      return -1;
    }
  }
  isAttribIndexed(name) {
    return this._coreGeometry.isAttribIndexed(name);
  }
  position() {
    return this._position || (this._position = this.getPosition(new Vector3()));
  }
  getPosition(target) {
    const { array } = this._geometry.getAttribute(ATTRIB_NAMES.POSITION);
    return target.fromArray(array, this._index * 3);
  }
  setPosition(new_position) {
    this.setAttribValueVector3(ATTRIB_NAMES.POSITION, new_position);
  }
  normal() {
    return this._normal = this._normal || this.getNormal(new Vector3());
  }
  getNormal(target) {
    const { array } = this._geometry.getAttribute(ATTRIB_NAMES.NORMAL);
    return target.fromArray(array, this._index * 3);
  }
  setNormal(new_normal) {
    return this.setAttribValueVector3(ATTRIB_NAMES.NORMAL, new_normal);
  }
  setAttribValue(attribName, value) {
    const attrib = this._geometry.getAttribute(attribName);
    const array = attrib.array;
    const attribSize = attrib.itemSize;
    if (CoreType.isArray(value)) {
      for (let i = 0; i < attribSize; i++) {
        array[this._index * attribSize + i] = value[i];
      }
      return;
    }
    switch (attribSize) {
      case 1:
        array[this._index] = value;
        break;
      case 2:
        const v2 = value;
        const i2 = this._index * 2;
        array[i2 + 0] = v2.x;
        array[i2 + 1] = v2.y;
        break;
      case 3:
        const isColor = value.r != null;
        const i3 = this._index * 3;
        if (isColor) {
          const col = value;
          array[i3 + 0] = col.r;
          array[i3 + 1] = col.g;
          array[i3 + 2] = col.b;
        } else {
          const v3 = value;
          array[i3 + 0] = v3.x;
          array[i3 + 1] = v3.y;
          array[i3 + 2] = v3.z;
        }
        break;
      case 4:
        const v4 = value;
        const i4 = this._index * 4;
        array[i4 + 0] = v4.x;
        array[i4 + 1] = v4.y;
        array[i4 + 2] = v4.z;
        array[i4 + 3] = v4.w;
        break;
      default:
        console.warn(`Point.set_attrib_value does not yet allow attrib size ${attribSize}`);
        throw `attrib size ${attribSize} not implemented`;
    }
  }
  setAttribValueFromNumber(attribName, value) {
    const attrib = this._geometry.getAttribute(attribName);
    const array = attrib.array;
    array[this._index] = value;
  }
  setAttribValueFromVector2(attribName, value) {
    const attrib = this._geometry.getAttribute(attribName);
    value.toArray(attrib.array, this._index * 2);
  }
  setAttribValueFromVector3(attribName, value) {
    const attrib = this._geometry.getAttribute(attribName);
    value.toArray(attrib.array, this._index * 3);
  }
  setAttribValueFromVector4(attribName, value) {
    const attrib = this._geometry.getAttribute(attribName);
    value.toArray(attrib.array, this._index * 4);
  }
  setAttribValueVector3(name, value) {
    if (value == null) {
      return;
    }
    if (name == null) {
      throw "Point.set_attrib_value requires a name";
    }
    const attrib = this._geometry.getAttribute(name);
    const array = attrib.array;
    const i = this._index * 3;
    array[i] = value.x;
    array[i + 1] = value.y;
    array[i + 2] = value.z;
  }
  setAttribIndex(name, new_value_index) {
    const array = this._geometry.getAttribute(name).array;
    return array[this._index] = new_value_index;
  }
}
